import { useNavigate } from "react-router-dom";
import { useSyncExternalStore } from "react";
import {
  displayNameFromEmail,
  initialsFromEmail,
  initialsFromLabel,
  participantColorByEmail,
  waveSaveStateLabel,
} from "@vida/wave-core";
import {
  currentUserEmail,
  currentUserSub,
  loadTokens,
  startLogin,
} from "../../auth";
import {
  EMPTY_ACTIVITY,
  getActivityLog,
  getRealtimeStatus,
  getWaveSaveState,
  subscribeActivity,
  subscribeRealtimeStatus,
  subscribeWaveSaveState,
} from "../../services/waveSession";

type Props = {
  presenceError?: string | null;
};

export function WaveSidebarBottomCards({ presenceError: _presenceError = null }: Props) {
  const navigate = useNavigate();
  const activity = useSyncExternalStore(
    subscribeActivity,
    getActivityLog,
    () => EMPTY_ACTIVITY,
  );
  const realtimeStatus = useSyncExternalStore(
    subscribeRealtimeStatus,
    getRealtimeStatus,
    () => "offline" as const,
  );
  const saveState = useSyncExternalStore(
    subscribeWaveSaveState,
    getWaveSaveState,
    () => "synced" as const,
  );
  const loggedIn = Boolean(loadTokens());
  const email = currentUserEmail();
  const sub = currentUserSub();
  const displayName = email ? displayNameFromEmail(email) : "Explorer";
  const initials = email
    ? initialsFromEmail(email)
    : initialsFromLabel(displayName, null);
  const avatarColor = participantColorByEmail(email, sub ?? "anonymous").hex;

  const pendingFromActivity = activity.filter((a) => a.detail.includes("…")).length;
  const activityPrimary =
    pendingFromActivity > 0
      ? pendingFromActivity === 1 && activity[0]
        ? activity[0].title
        : `${pendingFromActivity} attività in corso`
      : "Attività";
  const activitySecondary =
    pendingFromActivity > 0
      ? activity[0]?.detail ?? "Job cloud in corso"
      : "Tocca per cronologia e sync";

  return (
    <div className="wave-sidebar-bottom">
      <div className="wave-sidebar-sync-mini" aria-label="Stato connessione e salvataggio">
        <span className={`realtime-badge realtime-${realtimeStatus}`}>
          <span className="realtime-dot" aria-hidden />
          <span>{realtimeStatus === "online" ? "Live" : realtimeStatus === "polling" ? "Sync" : "Offline"}</span>
        </span>
        <span className={`save-badge save-${saveState}`}>
          <span className="save-dot" aria-hidden />
          <span>{waveSaveStateLabel(saveState)}</span>
        </span>
      </div>
      <button
        type="button"
        className="wave-sidebar-card wave-account-card"
        onClick={() => {
          if (loggedIn) navigate("/account");
          else startLogin();
        }}
      >
        <span
          className="wave-account-avatar"
          style={{ backgroundColor: avatarColor }}
          aria-hidden
        >
          {initials}
        </span>
        <span className="wave-sidebar-card-text">
          <strong>{loggedIn ? displayName : "Accedi"}</strong>
          <span className="wave-account-email muted">
            {loggedIn ? (email ?? "Account VIDA") : "Tocca per accedere"}
          </span>
        </span>
        <span className="wave-gallery-chevron" aria-hidden>
          ›
        </span>
      </button>

      <button
        type="button"
        className="wave-sidebar-card wave-activity-card"
        onClick={() => navigate("/activity")}
      >
        <span className="wave-activity-icon" aria-hidden>
          {pendingFromActivity > 0 ? "◌" : "〰"}
        </span>
        <span className="wave-sidebar-card-text">
          <strong>{activityPrimary}</strong>
          <span className="muted">{activitySecondary}</span>
        </span>
        <span className="wave-gallery-chevron" aria-hidden>
          ›
        </span>
      </button>
    </div>
  );
}
